import { FC, MouseEventHandler, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Stack, Typography } from '@mui/material';
import i18n from 'i18next';

import { useLiveUpdateVersion, useUser } from '@mopla/business-logic';
import {
	ESubscriptionStatus,
	TPaymentType,
	TSubscription,
} from '@mopla/data-models';
import { PaletteColors } from '@mopla/mui';

import { MplButton } from '../../atoms/MplButton/MplButton';
import { GlobeIcon } from '../../icons/Globe';
import { PaymentIcon } from '../../icons/PaymentIcon';
import { ProfileCircleIcon } from '../../icons/ProfileCircle';
import { QuestionMarkOutlined } from '../../icons/QuestionMarkOutlined';
import { ProfileGenericButton } from '../../molecules/ProfileGenericButton/ProfileGenericButton';

import { useProfileManagerContext } from './context';
import { DriverFeedbackGenericButton } from './DriverReviewPage';
import {
	ContentWrapperFlex,
	ImprintButton,
	InitialScreenRoot,
	UserAvatar,
	UserGreeting,
	UserGreetingWrapper,
} from './ProfileManager.styles';

const stringAvatar = (name: string) => {
	return {
		sx: {
			bgcolor: PaletteColors.secondary.main,
		},
		children: `${name.split(' ')[0]?.[0] || ''}${
			name.split(' ')[1]?.[0] || ''
		}`,
	};
};

export interface InitialScreenProps {
	onLogout: VoidFunction;
	onProfile: VoidFunction;
	onPayment: VoidFunction;
	onLanguage: VoidFunction;
	onReviews?: VoidFunction;
	onImprint?: MouseEventHandler<HTMLButtonElement>;
	onCookieSettings?: MouseEventHandler<HTMLButtonElement>;
	onTransportTypes: VoidFunction;
	onFAQ?: MouseEventHandler<HTMLButtonElement>;
	devFeatureFlagsTriggerNode?: ReactNode;
	showGreeting?: boolean;
	subscription?: TSubscription;
	defaultPaymentMethodType?: TPaymentType;
	showLiveUpdatesVersion?: boolean;
}

export const InitialProfileScreen: FC<InitialScreenProps> = ({
	onLogout,
	onProfile,
	onPayment,
	onReviews,
	onLanguage,
	onImprint,
	onCookieSettings,
	onFAQ,
	devFeatureFlagsTriggerNode,
	showGreeting,
	subscription,
	defaultPaymentMethodType,
	showLiveUpdatesVersion = false,
	onTransportTypes,
}) => {
	const { t } = useTranslation(['profile', 'common']);
	const { userData } = useUser();

	const { liveUpdateVersion } = useLiveUpdateVersion();

	const { isDriverApp, isPassengerApp } = useProfileManagerContext();
	const isPendingSubscription =
		subscription?.status === ESubscriptionStatus.PENDING;

	const hasDefaultPaymentMethod = !!userData?.hasDefaultStripePaymentMethod;

	const getPaymentHelperText = () => {
		if (!hasDefaultPaymentMethod || !defaultPaymentMethodType) return '';

		return t(`profile:subheader.payment_method.${defaultPaymentMethodType}`);
	};

	const windowRef = window as any;

	return (
		<InitialScreenRoot data-testid="initialScreen-root">
			<ContentWrapperFlex>
				{showGreeting ? (
					<UserGreetingWrapper>
						<UserAvatar
							data-testid="initialScreen-userAvatar"
							{...stringAvatar(
								`${userData?.firstName} ${userData?.lastName}` || ''
							)}
						/>
						<UserGreeting data-testid="initialScreen-userGreeting">
							<span>{t('common:text.hello')},</span>
							<span>
								{userData?.firstName} {userData?.lastName}
							</span>
						</UserGreeting>
					</UserGreetingWrapper>
				) : null}
				{!isDriverApp ? (
					<ProfileGenericButton
						testId="initialScreen-myDetailsButton"
						onClick={onProfile}
						mainIcon={<ProfileCircleIcon />}
						label={t('profile:button.my_details')}
						variant="grey"
					/>
				) : null}
				{!isDriverApp ? (
					<ProfileGenericButton
						testId="initialScreen-paymentButton"
						onClick={onPayment}
						mainIcon={<PaymentIcon />}
						label={t('profile:button.payment')}
						variant="grey"
						helperText={getPaymentHelperText()}
						disabled={isPendingSubscription}
					/>
				) : null}
				{isDriverApp && onReviews && (
					<DriverFeedbackGenericButton onReviewsClick={onReviews} />
				)}
				<ProfileGenericButton
					testId="initialScreen-languageButton"
					onClick={onLanguage}
					mainIcon={<GlobeIcon />}
					label={t('profile:button.language')}
					helperText={t(`profile:button.${i18n.language}`)}
					variant={isDriverApp ? 'white' : 'grey'}
				/>
				<ProfileGenericButton
					testId="initialScreen-helpButton"
					onClick={onTransportTypes}
					mainIcon={<QuestionMarkOutlined />}
					label={t('profile:button.help_center')}
					variant="white"
				/>
			</ContentWrapperFlex>
			{!isDriverApp && (
				<Stack gap={1.5} mb={1.5} alignItems="center">
					{devFeatureFlagsTriggerNode}

					<ImprintButton data-testid="initialScreen-faqButton" onClick={onFAQ}>
						<Typography variant="body2" color="neutral.0">
							{t('profile:button.faq')}
						</Typography>
					</ImprintButton>

					<ImprintButton data-testid="initialScreen-button" onClick={onImprint}>
						<Typography variant="body2" color="neutral.0">
							{t('profile:button.imprint')}
						</Typography>
					</ImprintButton>

					{windowRef.CookieScript && (
						<ImprintButton
							data-testid="initialScreen-cookiesButton"
							onClick={onCookieSettings}
						>
							<Typography variant="body2" color="neutral.0">
								{t('profile:button.cookie_settings')}
							</Typography>
						</ImprintButton>
					)}

					{showLiveUpdatesVersion && (
						<Typography
							data-testid="initialScreen-liveUpdatesText"
							variant="body2"
							color={isPassengerApp ? 'neutral.0' : 'primary'}
						>
							{Capacitor.isNativePlatform()
								? `Live Updates build: ${liveUpdateVersion?.buildId}`
								: `Web build: ${process.env.NX_WEB_BUILD_DIST}`}
						</Typography>
					)}
				</Stack>
			)}
			<Stack alignItems="center" px={2} pb={3}>
				<MplButton
					testId="initialScreen-logoutButton"
					variant="text"
					onClick={onLogout}
					color={isPassengerApp ? 'primaryRevert' : 'primary'}
				>
					{t('profile:button.logout')}
				</MplButton>
			</Stack>
		</InitialScreenRoot>
	);
};
